import type {
  FairUseConfig,
  FairUseImagePrices,
  FairUseVoiceTokenKind,
  FairUseVoiceTokenPrices,
} from "@/api/admin/fair-use"

import {
  FAIR_USE_MAX_IMAGE_PRICE_SEK,
  FAIR_USE_MAX_VOICE_TOKEN_PRICE_SEK_PER_1M,
  FAIR_USE_VOICE_TOKEN_PRICE_FIELDS,
} from "@/api/admin/fair-use"

// Non-component helpers for the two ROUND-3 price tables (#3089, spec R5/R6) —
// Configuration → Fair use → Plans & pricing, below the round-2 voice rate.
//
// Their own file rather than an extension of `fair-use-entitlements-utils.ts`:
// that file is at the size limit, and these two tables share nothing with the
// matrix except the surface they render on. The dependency runs one way —
// the entitlement matrix borrows {@link parseFairUseSekLiteral} from here, so
// every SEK box on this config agrees on what a number looks like.
//
// Both tables are edited as a FLAT LIST OF ROWS and only assembled into the
// nested wire shape at save time. The alternative — editing the nested object
// in place — cannot represent the states an admin passes through while typing
// (a half-named model, a size with no quality yet), and the backend rejects an
// empty inner object with a 400, so those states have to be unrepresentable
// rather than merely invalid.

/**
 * A plain decimal, comma or point, no grouping — the fair-use surface's own
 * rule for money.
 *
 * Deliberately not `Number(value)`: that accepts "1e3", "0x10", "Infinity" and
 * a leading "+", every one of which is a typo on a price the invoice is
 * computed from.
 */
const SEK_LITERAL = /^\d+([.,]\d+)?$/

/** One SEK amount as a number, or `null` when it is not a plain decimal. */
export function parseFairUseSekLiteral(value: string): number | null {
  const trimmed = value.trim()
  if (!SEK_LITERAL.test(trimmed)) return null
  const parsed = Number(trimmed.replace(",", "."))
  return Number.isFinite(parsed) ? parsed : null
}

/** One price inside an inclusive `[0, ceiling]` span, or `null`. */
function parseBoundedSek(value: string, ceiling: number): number | null {
  const parsed = parseFairUseSekLiteral(value)
  if (parsed == null || parsed < 0 || parsed > ceiling) return null
  return parsed
}

// =============================================================================
// Image prices — {model: {size: {quality: SEK per image}}}
// =============================================================================

/**
 * One editable image price.
 *
 * `key` is a client-side row identity so React keeps the inputs mounted while
 * the admin retypes a model id — keying on the id itself would remount (and
 * blur) the field on every keystroke, exactly as it would in the voice table.
 */
export type FairUseImagePriceRow = {
  key: string
  model: string
  size: string
  quality: string
  price: string
}

let imageRowCounter = 0

/** One blank row, or one built from a stored price. */
export function fairUseImagePriceRow(
  values: { model?: string; size?: string; quality?: string; price?: string } = {}
): FairUseImagePriceRow {
  imageRowCounter += 1
  return {
    key: `image-price-${imageRowCounter}`,
    model: values.model ?? "",
    size: values.size ?? "",
    quality: values.quality ?? "",
    price: values.price ?? "",
  }
}

/** `default` sorts first at every level, the rest alphabetically. */
function byDefaultFirst(left: string, right: string): number {
  if (left === right) return 0
  if (left === "default") return -1
  if (right === "default") return 1
  return left < right ? -1 : 1
}

/** The stored three-level table as a flat, stably ordered list of rows. */
export function fairUseImagePricesToFormState(
  prices: FairUseImagePrices | undefined
): FairUseImagePriceRow[] {
  const rows: FairUseImagePriceRow[] = []
  for (const model of Object.keys(prices ?? {}).sort(byDefaultFirst)) {
    const sizes = prices?.[model] ?? {}
    for (const size of Object.keys(sizes).sort(byDefaultFirst)) {
      const qualities = sizes[size] ?? {}
      for (const quality of Object.keys(qualities).sort(byDefaultFirst)) {
        rows.push(fairUseImagePriceRow({ model, size, quality, price: String(qualities[quality]) }))
      }
    }
  }
  return rows
}

export type FairUseImagePricesIssue = "key" | "duplicate" | "range" | null

/**
 * What is wrong with the image table, if anything.
 *
 * Every level is compared LOWER-CASED because the backend lower-cases all
 * three on write and refuses a case-insensitive duplicate with a 400 — at any
 * level, since a `{"HIGH": 4, "high": 1}` quality map is the same coin flip as
 * a duplicate model id.
 */
export function fairUseImagePricesIssue(rows: FairUseImagePriceRow[]): FairUseImagePricesIssue {
  const seen = new Set<string>()
  for (const row of rows) {
    const model = row.model.trim().toLowerCase()
    const size = row.size.trim().toLowerCase()
    const quality = row.quality.trim().toLowerCase()
    if (!model || !size || !quality) return "key"
    const triple = JSON.stringify([model, size, quality])
    if (seen.has(triple)) return "duplicate"
    seen.add(triple)
    if (parseBoundedSek(row.price, FAIR_USE_MAX_IMAGE_PRICE_SEK) == null) return "range"
  }
  return null
}

/** The rows as the nested wire payload, or `null` when one is unusable. */
export function fairUseFormStateToImagePrices(
  rows: FairUseImagePriceRow[]
): FairUseImagePrices | null {
  if (fairUseImagePricesIssue(rows) !== null) return null
  const prices: FairUseImagePrices = {}
  for (const row of rows) {
    const model = row.model.trim().toLowerCase()
    const size = row.size.trim().toLowerCase()
    const quality = row.quality.trim().toLowerCase()
    const sizes = (prices[model] ??= {})
    const qualities = (sizes[size] ??= {})
    qualities[quality] = parseBoundedSek(row.price, FAIR_USE_MAX_IMAGE_PRICE_SEK) as number
  }
  return prices
}

/**
 * Whether the platform stored the image table that was sent.
 *
 * Structural, like the matrix and voice-rate twins: two objects off two round
 * trips are never the same reference, so an identity check would report every
 * save as discarded and drown the real "the platform dropped a field" signal.
 */
export function fairUseImagePricesApplied(
  sent: FairUseImagePrices,
  saved: FairUseImagePrices | undefined
): boolean {
  if (!saved) return false
  const models = Object.keys(sent)
  if (models.length !== Object.keys(saved).length) return false
  return models.every((model) => {
    // `Object.hasOwn` rather than a truthiness check: an index into a
    // `Record<string, …>` is typed as present whether or not it is, so the
    // absent level has to be asked about by name.
    if (!Object.hasOwn(saved, model)) return false
    const sentSizes = sent[model]
    const savedSizes = saved[model]
    const sizes = Object.keys(sentSizes)
    if (sizes.length !== Object.keys(savedSizes).length) return false
    return sizes.every((size) => {
      if (!Object.hasOwn(savedSizes, size)) return false
      const sentQualities = sentSizes[size]
      const savedQualities = savedSizes[size]
      const qualities = Object.keys(sentQualities)
      if (qualities.length !== Object.keys(savedQualities).length) return false
      return qualities.every((quality) => savedQualities[quality] === sentQualities[quality])
    })
  })
}

/** Whether this config carries the round-3 image table at all. */
export function fairUseHasImagePrices(config: Pick<FairUseConfig, "image_price_sek">): boolean {
  return !!config.image_price_sek && typeof config.image_price_sek === "object"
}

// =============================================================================
// Voice token prices — {model: {kind: SEK per 1M tokens}}
// =============================================================================

/**
 * One editable voice model, with a cell per token kind.
 *
 * The rates are held as a complete map over the five kinds — a kind the admin
 * leaves blank is simply not sent, and the platform prices it at 0 — so a new
 * platform kind cannot silently lose the four that were typed next to it.
 */
export type FairUseVoiceTokenPriceRow = {
  key: string
  id: string
  rates: Record<FairUseVoiceTokenKind, string>
}

let voiceTokenRowCounter = 0

function emptyRates(): Record<FairUseVoiceTokenKind, string> {
  const rates = {} as Record<FairUseVoiceTokenKind, string>
  for (const kind of FAIR_USE_VOICE_TOKEN_PRICE_FIELDS) rates[kind] = ""
  return rates
}

export function fairUseVoiceTokenPriceRow(
  id = "",
  rates?: Partial<Record<FairUseVoiceTokenKind, string>>
): FairUseVoiceTokenPriceRow {
  voiceTokenRowCounter += 1
  return { key: `voice-token-${voiceTokenRowCounter}`, id, rates: { ...emptyRates(), ...rates } }
}

/** The stored table as editable rows, `default` first and the rest sorted. */
export function fairUseVoiceTokenPricesToFormState(
  prices: FairUseVoiceTokenPrices | undefined
): FairUseVoiceTokenPriceRow[] {
  return Object.keys(prices ?? {})
    .sort(byDefaultFirst)
    .map((id) => {
      const stored = prices?.[id] ?? {}
      const rates: Partial<Record<FairUseVoiceTokenKind, string>> = {}
      for (const kind of FAIR_USE_VOICE_TOKEN_PRICE_FIELDS) {
        const rate = stored[kind]
        // An absent kind stays an EMPTY box rather than becoming "0": the two
        // price identically today, but only one of them is something the
        // platform was told.
        if (typeof rate === "number") rates[kind] = String(rate)
      }
      return fairUseVoiceTokenPriceRow(id, rates)
    })
}

export type FairUseVoiceTokenPricesIssue = "id" | "duplicate" | "empty" | "range" | null

/**
 * What is wrong with the token table, if anything.
 *
 * `empty` is its own answer because the backend refuses a model whose rate map
 * is empty (400): the read path drops such a model, which would silently move
 * it back to per-minute billing under a successful save.
 */
export function fairUseVoiceTokenPricesIssue(
  rows: FairUseVoiceTokenPriceRow[]
): FairUseVoiceTokenPricesIssue {
  const seen = new Set<string>()
  for (const row of rows) {
    const id = row.id.trim().toLowerCase()
    if (!id) return "id"
    if (seen.has(id)) return "duplicate"
    seen.add(id)
    let filled = 0
    for (const kind of FAIR_USE_VOICE_TOKEN_PRICE_FIELDS) {
      const value = row.rates[kind].trim()
      if (value === "") continue
      filled += 1
      if (parseBoundedSek(value, FAIR_USE_MAX_VOICE_TOKEN_PRICE_SEK_PER_1M) == null) return "range"
    }
    if (filled === 0) return "empty"
  }
  return null
}

/** The rows as the wire payload, or `null` when one is unusable. */
export function fairUseFormStateToVoiceTokenPrices(
  rows: FairUseVoiceTokenPriceRow[]
): FairUseVoiceTokenPrices | null {
  if (fairUseVoiceTokenPricesIssue(rows) !== null) return null
  const prices: FairUseVoiceTokenPrices = {}
  for (const row of rows) {
    const rates: Partial<Record<FairUseVoiceTokenKind, number>> = {}
    for (const kind of FAIR_USE_VOICE_TOKEN_PRICE_FIELDS) {
      const value = row.rates[kind].trim()
      if (value === "") continue
      rates[kind] = parseBoundedSek(value, FAIR_USE_MAX_VOICE_TOKEN_PRICE_SEK_PER_1M) as number
    }
    prices[row.id.trim().toLowerCase()] = rates
  }
  return prices
}

/** Whether the platform stored the token table that was sent (see the twins). */
export function fairUseVoiceTokenPricesApplied(
  sent: FairUseVoiceTokenPrices,
  saved: FairUseVoiceTokenPrices | undefined
): boolean {
  if (!saved) return false
  const ids = Object.keys(sent)
  if (ids.length !== Object.keys(saved).length) return false
  return ids.every((id) => {
    if (!Object.hasOwn(saved, id)) return false
    const sentRates = sent[id]
    const savedRates = saved[id]
    const kinds = Object.keys(sentRates) as FairUseVoiceTokenKind[]
    if (kinds.length !== Object.keys(savedRates).length) return false
    return kinds.every((kind) => savedRates[kind] === sentRates[kind])
  })
}

/** The DOM id of one rate box, so the table and its tests agree on one name. */
export function fairUseVoiceTokenCellId(index: number, kind: FairUseVoiceTokenKind): string {
  return `fair-use-voice-token-${index}-${kind.replace(/_/g, "-")}`
}

/** Whether this config carries the round-3 token table at all. */
export function fairUseHasVoiceTokenPrices(
  config: Pick<FairUseConfig, "voice_token_prices">
): boolean {
  return !!config.voice_token_prices && typeof config.voice_token_prices === "object"
}
